import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Building2, Users, Activity, TrendingUp, ArrowUpRight, Clock, ExternalLink } from 'lucide-react';
import { useSuperadminDashboard } from '@/hooks/useSuperadmin';
import { format } from 'date-fns';

const PLAN_COLORS: Record<string, string> = {
  trial:      'hsl(var(--chart-1))',
  starter:    'hsl(var(--chart-2))',
  pro:        'hsl(var(--chart-3))',
  enterprise: 'hsl(var(--chart-4))',
};

const PlanBadge = ({ plan }: { plan: string }) => {
  const variants: Record<string, string> = {
    trial:      'bg-muted text-muted-foreground',
    starter:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    pro:        'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${variants[plan] ?? variants.trial}`}>
      {plan}
    </span>
  );
};

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const { tenants, activity, planDist, growth, loading, totalUsers, totalMAU } = useSuperadminDashboard();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Platform Dashboard</h1>
        <p className="text-muted-foreground text-sm">Real-time overview across all organisations</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Organisations', value: tenants.length, icon: Building2, delta: null },
          { label: 'Total Users',         value: totalUsers,     icon: Users,     delta: null },
          { label: 'Monthly Active',      value: totalMAU,       icon: Activity,  delta: null },
          { label: 'Estimated MRR',       value: `$${(tenants.filter(t => t.plan === 'pro').length * 299 + tenants.filter(t => t.plan === 'enterprise').length * 999 + tenants.filter(t => t.plan === 'starter').length * 49).toLocaleString()}`, icon: TrendingUp, delta: null },
        ].map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{kpi.label}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{kpi.value}</div>
                <p className="text-xs text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant growth area chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Organisation Growth</CardTitle>
            <CardDescription>New orgs per month (last 6 months)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={growth}>
                <defs>
                  <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Area type="monotone" dataKey="new_tenants" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#growthGrad)" name="New Orgs" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Plan distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
            <CardDescription>Orgs by plan tier</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={planDist} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={65} label={({ plan, percent }) => `${plan} ${(percent * 100).toFixed(0)}%`} fontSize={11}>
                  {planDist.map((entry) => (
                    <Cell key={entry.plan} fill={PLAN_COLORS[entry.plan] ?? 'hsl(var(--muted))'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {planDist.map((p) => (
                <div key={p.plan} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: PLAN_COLORS[p.plan] ?? 'hsl(var(--muted))' }} />
                    <span className="capitalize text-muted-foreground">{p.plan}</span>
                  </div>
                  <span className="font-medium">{p.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: tenant table + activity feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant table */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Organisations</CardTitle>
              <CardDescription>All tenants on the platform</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/superadmin/tenants')}>
              View all <ExternalLink className="ms-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Organisation</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Plan</th>
                  <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">Users</th>
                  <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">MAU</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tenants.slice(0, 8).map((t) => (
                  <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-foreground">{t.name}</div>
                      <div className="text-xs text-muted-foreground">/{t.slug}</div>
                    </td>
                    <td className="px-6 py-3"><PlanBadge plan={t.plan} /></td>
                    <td className="px-6 py-3 text-end font-medium">{t.total_users}</td>
                    <td className="px-6 py-3 text-end font-medium">{t.mau}</td>
                    <td className="px-6 py-3 text-muted-foreground">{format(new Date(t.created_at), 'MMM d, yyyy')}</td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">No organisations yet</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Activity feed */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Cross-tenant events</CardDescription>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-xs text-muted-foreground">Live</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[360px] overflow-y-auto pe-1">
            {activity.map((a) => (
              <div key={a.activity_id} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ArrowUpRight className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-foreground leading-tight truncate">
                    <span className="font-medium">{a.user_email?.split('@')[0] ?? 'User'}</span>
                    {' '}<span className="text-muted-foreground">{a.action_type.replace(/_/g, ' ')}</span>
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{a.tenant_slug ?? '—'}</Badge>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {format(new Date(a.created_at), 'h:mm a')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {activity.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperadminDashboard;
